import React, { useEffect, useState, useRef, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { Outlet, useLocation } from "react-router-dom";
import TimelineIcon from "../assets/Timeline.png";
import "../styles/UserHome.css";
import TimelineBar from "./TimelineBar";
import HubSetupBanner from "../components/HubSetupBanner";
import AdminAppShell from "../components/shells/AdminAppShell";
import { GuidedJourneyProvider } from "../components/GuidedJourney";
import { Tooltip, Button, Drawer, Modal, Grid } from "antd";

const { useBreakpoint } = Grid;

function resolveBreadcrumbLabel(pathname) {
  if (pathname === "/dashboard") return "Dashboard";
  if (pathname === "/hub") return "My Hub";
  if (pathname.startsWith("/biz")) return "Store";
  if (pathname.includes("gallery")) return "Media Gallery";
  if (pathname === "/subscription") return "Subscription";
  return "Page";
}

const UserHomeContent = () => {
  const { logout, token, decodeToken, setToken } = useAuth();
  const decodedToken = decodeToken(token);
  const [timelineVisible, setTimelineVisible] = useState(false);
  const [timelineDrawerWidth, setTimelineDrawerWidth] = useState(420);
  const timelineResizeRef = useRef({ active: false, startX: 0, startWidth: 0 });
  const [hubSetupVisible, setHubSetupVisible] = useState(false);
  const [confirmExitVisible, setConfirmExitVisible] = useState(false);

  // Check if MyHubAdmin/StaffAssignments are empty and show hub setup banner
  const needsHubSetup = useMemo(() => {
    if (!decodedToken) return false;
    const myHubAdmin =
      decodedToken.MyHubAdmin;
    const staffAssignments =
      decodedToken.StaffAssignments;

    const isEmptyValue = (value) => {
      if (!value) return true;
      if (Array.isArray(value)) return value.length === 0;
      if (typeof value === 'object') return Object.keys(value).length === 0;
      return false;
    };

    const noMyHubAdmin = isEmptyValue(myHubAdmin);
    const noStaffAssignments = isEmptyValue(staffAssignments);

    // Show banner only when both are empty
    return noMyHubAdmin && noStaffAssignments;
  }, [decodedToken]);


  const screens = useBreakpoint();
  const isMobile = screens.md === false;
  const location = useLocation();

  // Show/hide hub setup banner based on MyHubAdmin/StaffAssignments
  useEffect(() => {
    if (needsHubSetup && token) {
      setHubSetupVisible(true);
      return;
    }
    if (!needsHubSetup) {
      setHubSetupVisible(false);
    }
  }, [needsHubSetup, token]);

  // Handle hub setup completion
  const handleHubSetupComplete = async (newToken) => {
    if (newToken) {
      setToken(newToken);
    } else {
      window.location.reload();
    }
    setHubSetupVisible(false);
  };

  // Handle hub setup banner close attempt
  const handleHubSetupClose = () => {
    // Show confirmation modal instead of using Modal.confirm
    setConfirmExitVisible(true);
  };

  // Handle confirmation dialog
  const handleConfirmExit = () => {
    setConfirmExitVisible(false);
    setHubSetupVisible(false);
    logout();
  };

  const handleCancelExit = () => {
    setConfirmExitVisible(false);
    // Keep hub setup modal open
  };

  // Resize Activity Timeline drawer by dragging the left edge
  useEffect(() => {
    const onMove = (e) => {
      if (!timelineResizeRef.current.active) return;
      const delta = timelineResizeRef.current.startX - e.clientX;
      const next = Math.min(900, Math.max(320, timelineResizeRef.current.startWidth + delta));
      setTimelineDrawerWidth(next);
    };
    const onUp = () => {
      timelineResizeRef.current.active = false;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  const showTimeline = location.pathname === '/dashboard';
  const title = resolveBreadcrumbLabel(location.pathname);

  return (
    <>
      <AdminAppShell pageTitle={title}>
        {/* Main Content */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Outlet />
        </div>

        {/* Floating: Activity Timeline (top-right) — hidden, code kept */}
        {(false && (isMobile || showTimeline)) && (
          <div className="microapp-bar">
            {isMobile ? (
              <Button shape="circle" icon={<img src={TimelineIcon} alt="Timeline" style={{width:18,height:18}} />} onClick={() => setTimelineVisible(true)} />
            ) : (
              <Tooltip title="Activity Timeline" placement="left">
                <Button shape="circle" icon={<img src={TimelineIcon} alt="Timeline" style={{width:18,height:18}} />} onClick={() => setTimelineVisible((v) => !v)} />
              </Tooltip>
            )}
          </div>
        )}


      </AdminAppShell>

      {/* Activity Timeline – resizable drawer */}
      {showTimeline && (
        <Drawer title="Activity Timeline" placement="right" onClose={() => setTimelineVisible(false)} open={timelineVisible} width={timelineDrawerWidth}
          styles={{body:{paddingLeft:0,position:"relative"}}}
        >
          <div role="presentation" aria-label="Resize drawer"
            onMouseDown={(e) => {
              if (e.button !== 0) return;
              e.preventDefault();
              timelineResizeRef.current = {active:true,startX:e.clientX,startWidth:timelineDrawerWidth};
            }}
            style={{position:"absolute",left:0,top:0,bottom:0,width:8,cursor:"col-resize",zIndex:1}}
          />
          <div style={{paddingLeft:14}}>
            <TimelineBar CreatedOn={decodedToken?.MyHubAdmin[1].HubProfile?.CreatedOn} />
          </div>
        </Drawer>
      )}


      {/* Hub Setup Banner Modal */}
      <HubSetupBanner visible={hubSetupVisible} onComplete={handleHubSetupComplete} onCancel={handleHubSetupClose} />

      {/* Confirmation Exit Modal */}
      <Modal title="Exit Hub Setup?" open={confirmExitVisible} onOk={handleConfirmExit} onCancel={handleCancelExit}
        okText="Yes, Exit" okType="danger" cancelText="No, Continue Setup" maskClosable={false} getContainer={() => document.body}
      >
        <p>You can't move ahead without completing Hub Setup.</p>
      </Modal>
    </>
  );
};

const UserHome = () => (
  <GuidedJourneyProvider>
    <UserHomeContent />
  </GuidedJourneyProvider>
);

export default UserHome;
